import React, { useState, useRef, useEffect } from "react";
import { useGame, GamePhase, Message } from "../../context/GameContext";

interface ChatPanelProps {
  messages: Message[];
  isAlive: boolean;
  phase: GamePhase;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ messages, isAlive, phase }) => {
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { sendMessage } = useGame();

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) return;

    sendMessage(message);
    setMessage("");
  };

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const canChat = isAlive || phase === GamePhase.NIGHT;

  return (
    <div className="card-transparent h-full flex flex-col">
      <h2 className="text-lg font-semibold mb-4">Chat</h2>

      <div className="flex-grow overflow-y-auto mb-4 max-h-[400px] scrollbar-thin scrollbar-thumb-slate-700">
        <div className="space-y-3">
          {messages.length === 0 ? (
            <div className="text-slate-500 text-center p-4">
              No messages yet
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className="bg-slate-700 bg-opacity-30 rounded-lg p-3"
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="font-medium text-indigo-300">
                    {msg.sender}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-sm break-words text-white">{msg.content}</p>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="mt-auto w-full">
        <form onSubmit={handleSendMessage}>
          <div className="flex gap-2 w-full flex-wrap sm:flex-nowrap">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                canChat ? "Type your message..." : "You cannot chat now..."
              }
              className="input w-full sm:flex-grow"
              disabled={!canChat}
            />
            <button
              type="submit"
              className="btn btn-primary w-full sm:w-auto"
              disabled={!canChat || !message.trim()}
            >
              Send
            </button>
          </div>
          {!canChat && phase === GamePhase.DAY && (
            <p className="text-xs text-red-400 mt-1">
              Dead players cannot chat during the day
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ChatPanel;
